import { useEffect, useRef } from 'react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { consumeMagicLink } from '@/api/generated';
import { env } from '@/lib/env';
import { applySeo } from '@/lib/seo';

// Magic-link landing page (/auth/magic).
//
// The link in the sign-in email points here, on the GUEST origin, rather than
// at the API. On the API origin it sat outside the installed app's scope, and
// no browser can hand an out-of-scope link to a web app — so it could only
// ever open externally, which is half of why OH-149 existed.
//
// The token is spent by FETCH, not by navigating to the API. A guest who
// arrives inside the installed app has to stay inside it; a cross-origin
// navigation would throw them out to a browser and put the session in the
// wrong cookie jar — the exact failure this page exists to end.
//
// The API's own GET /auth/magic redirect is untouched and still works. Every
// email sent before this shipped points at it.
export const Route = createFileRoute('/auth/magic')({
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({
    token: typeof s.token === 'string' ? s.token : undefined,
  }),
  staticData: { chrome: false },
  component: MagicLinkPage,
});

// legacyConsumeUrl is the API's redirect endpoint — where the link used to
// point, and where this page falls back when our own consume cannot answer.
function legacyConsumeUrl(token: string): string {
  return `${env.apiOrigin}/api/v1/booking-engine/auth/magic?token=${encodeURIComponent(token)}`;
}

export function MagicLinkPage() {
  const { token } = useSearch({ strict: false }) as { token?: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // The token is single-use: running this twice would spend it and then report
  // the second attempt's failure. StrictMode double-invokes effects in dev.
  const startedRef = useRef(false);

  useEffect(() => {
    return applySeo({ title: 'Signing you in — ollio', noindex: true });
  }, []);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const raw = token ?? '';

    // Strip the token from the URL before anything else runs. It is a live
    // credential, and on this origin — unlike the API's — it would otherwise
    // reach the browser's history, the Referer of anything this page loads,
    // and PostHog's $current_url. (posthog.ts sanitises captures as well; this
    // is the half that also covers history and referrer.)
    window.history.replaceState(null, '', '/auth/magic');

    if (!raw) {
      void navigate({ to: '/login', search: { error: 'invalid' }, replace: true });
      return;
    }

    void (async () => {
      try {
        const { response } = await consumeMagicLink({ body: { token: raw } });

        if (response?.status === 204) {
          // Same reasoning as the code path on /login: the cabinet redirects
          // on a cached null profile, and a stale one would bounce this fresh
          // session straight back to the login page.
          queryClient.removeQueries({ queryKey: ['guest-profile'] });
          queryClient.removeQueries({ queryKey: ['my-bookings'] });
          void navigate({ to: '/cabinet', replace: true });
          return;
        }

        if (response?.status === 401) {
          void navigate({ to: '/login', search: { error: 'invalid' }, replace: true });
          return;
        }

        // Anything else — a 5xx, a proxy in the way — is OUR failure, not a bad
        // link. Hand the guest to the endpoint the link used to point at: it
        // signs them in and redirects, at the cost of leaving the app. A dead
        // end here would be worse, and the token is no more exposed than it
        // was before this page existed.
        window.location.href = legacyConsumeUrl(raw);
      } catch {
        window.location.href = legacyConsumeUrl(raw);
      }
    })();
  }, [token, navigate, queryClient]);

  return (
    <div className="flex min-h-full items-center justify-center px-6 py-12">
      <div className="text-center">
        <div
          aria-hidden="true"
          className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-[var(--ollio-navy)] border-t-transparent"
        />
        <p role="status" className="mt-4 text-muted">
          Signing you in…
        </p>
      </div>
    </div>
  );
}
